import { useEffect, useState } from "react";
import { z } from "zod";

export const ANALYTICS_CONSENT_KEY = "analytics-consent";
export const ANALYTICS_CONSENT_AT_KEY = "analytics-consent-at";
export const ANALYTICS_CONSENT_VERSION_KEY = "analytics-consent-version";
export const ANALYTICS_CONSENT_VERSION = "2026-10-12-limited-v1";
/** CNIL guidance: ask again six months after the visitor's choice. */
export const CONSENT_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 183;
export const ANALYTICS_CONSENT_EVENT = "analytics-consent-open";
export const ANALYTICS_CONSENT_CHANGED_EVENT = "analytics-consent-changed";

const consentStateSchema = z.enum(["pending", "granted", "denied"]);
export type ConsentState = z.infer<typeof consentStateSchema>;

const notifyConsentChange = () => {
	if (typeof window === "undefined") return;
	window.dispatchEvent(new Event(ANALYTICS_CONSENT_CHANGED_EVENT));
};

export const clearStoredAnalyticsConsent = () => {
	try {
		const keys = [
			ANALYTICS_CONSENT_KEY,
			ANALYTICS_CONSENT_AT_KEY,
			ANALYTICS_CONSENT_VERSION_KEY,
		];
		for (const key of keys) localStorage.removeItem(key);
	} catch {
		// No data can be collected while storage is inaccessible.
	}
};

export const readAnalyticsConsent = (): ConsentState => {
	if (typeof window === "undefined") return "pending";
	try {
		const stored = localStorage.getItem(ANALYTICS_CONSENT_KEY);
		const parsed = consentStateSchema.safeParse(stored);
		const decidedAt = Number(localStorage.getItem(ANALYTICS_CONSENT_AT_KEY));
		const age = Date.now() - decidedAt;
		const isFresh =
			decidedAt > 0 &&
			Number.isFinite(age) &&
			age >= 0 &&
			age < CONSENT_MAX_AGE_MS;
		const isCurrent =
			localStorage.getItem(ANALYTICS_CONSENT_VERSION_KEY) ===
			ANALYTICS_CONSENT_VERSION;

		if (parsed.success && parsed.data !== "pending" && isFresh && isCurrent) {
			return parsed.data;
		}
	} catch {
		// A browser that blocks storage must never implicitly enable analytics.
	}
	return "pending";
};

const storeConsent = (state: "granted" | "denied") => {
	try {
		localStorage.setItem(ANALYTICS_CONSENT_KEY, state);
		localStorage.setItem(ANALYTICS_CONSENT_AT_KEY, String(Date.now()));
		localStorage.setItem(
			ANALYTICS_CONSENT_VERSION_KEY,
			ANALYTICS_CONSENT_VERSION,
		);
	} catch {
		// A partial write must not retain a formerly granted choice.
		clearStoredAnalyticsConsent();
	}
};

export const useAnalyticsConsent = () => {
	const [state, setState] = useState<ConsentState | "loading">("loading");

	useEffect(() => {
		if (typeof window === "undefined") return;

		let expiry: ReturnType<typeof setTimeout> | undefined;
		const handleChange = () => {
			clearTimeout(expiry);
			const next = readAnalyticsConsent();
			if (next === "pending") clearStoredAnalyticsConsent();
			setState(next);
			if (next === "pending") return;
			const decidedAt = Number(localStorage.getItem(ANALYTICS_CONSENT_AT_KEY));
			// Browsers cap a timeout at about 25 days; recheck during long visits.
			expiry = setTimeout(
				() => {
					handleChange();
					notifyConsentChange();
				},
				Math.min(decidedAt + CONSENT_MAX_AGE_MS - Date.now(), 2_147_483_647),
			);
		};

		handleChange();
		window.addEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, handleChange);
		window.addEventListener("storage", handleChange);
		window.addEventListener("focus", handleChange);

		return () => {
			clearTimeout(expiry);
			window.removeEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, handleChange);
			window.removeEventListener("storage", handleChange);
			window.removeEventListener("focus", handleChange);
		};
	}, []);

	const grantConsent = () => {
		if (typeof window === "undefined") return;
		storeConsent("granted");
		notifyConsentChange();
	};

	const denyConsent = () => {
		if (typeof window === "undefined") return;
		storeConsent("denied");
		notifyConsentChange();
	};

	return { state, grantConsent, denyConsent };
};

export const useAnalyticsConsentState = () => useAnalyticsConsent().state;
